import React, { useEffect, useState } from 'react';
import GmModal from 'app/shared/modal/modal';
import { MultiSelect } from 'app/shared/select';
import { useIssueService } from 'hooks/api/agents/issue';
import { useIssueReportService } from 'hooks/api/agents/issue-report';
import { SeverityBadge, CategoryTag, JiraLink } from '../shared/badges';
import { AddTenantToIssueDialog } from '../shared/add-tenant-to-issue';
import { STATUS_OPTIONS, labelFor } from '../constants';
import styles from '../issues.module.css';

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

const reporterName = (report) =>
  report.reporter?.name ||
  [report.reporter?.firstname, report.reporter?.lastname].filter(Boolean).join(' ') ||
  report.reporter?.email ||
  '';

export const IssueDetailModal = ({
  issue: initial,
  onUpdated = () => {},
  closeModal = () => {}
}) => {
  const { fetchIssue, fetchIssueReports, updateIssue } = useIssueService();
  const { updateReportStatus } = useIssueReportService();

  const [issue, setIssue] = useState(initial);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showAddTenant, setShowAddTenant] = useState(false);

  const issue_id = initial.id || initial._id;

  const loadReports = () => {
    setLoadingReports(true);
    fetchIssueReports(issue_id, { query_string: 'sort_by=-reported_at&population=50' })
      .then(({ reports: fetched }) => setReports(fetched || []))
      .finally(() => setLoadingReports(false));
  };

  useEffect(() => {
    fetchIssue(issue_id).then(({ issue: fresh }) => {
      if (fresh) setIssue(fresh);
    });
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue_id]);

  const handleIssueStatus = async (option) => {
    if (!option || option.value === issue.status) return;
    const previous = issue.status;
    setIssue((prev) => ({ ...prev, status: option.value }));
    setSavingStatus(true);
    const { issue: updated } = await updateIssue(issue_id, { status: option.value });
    setSavingStatus(false);
    if (updated) {
      setIssue(updated);
      onUpdated(updated);
    } else {
      setIssue((prev) => ({ ...prev, status: previous }));
    }
  };

  const handleReportStatus = async (report, option) => {
    const id = report.id || report._id;
    if (!option || option.value === report.status) return;
    const previous = report.status;
    setReports((prev) =>
      prev.map((r) => ((r.id || r._id) === id ? { ...r, status: option.value } : r))
    );
    const { report: updated } = await updateReportStatus(id, option.value);
    if (!updated) {
      setReports((prev) =>
        prev.map((r) => ((r.id || r._id) === id ? { ...r, status: previous } : r))
      );
    }
  };

  const handleTenantAdded = () => {
    fetchIssue(issue_id).then(({ issue: fresh }) => {
      if (fresh) {
        setIssue(fresh);
        onUpdated(fresh);
      }
    });
    loadReports();
  };

  return (
    <div className={styles.detailWrapper}>
      <div className={styles.detailLeft}>
        <section className={styles.card}>
          <div className={styles.detailHeader}>
            <div className={styles.detailHeading}>
              <h2 className={styles.detailTitle}>{issue.title}</h2>
              <div className={styles.detailMetaRow}>
                <SeverityBadge value={issue.severity} />
                <CategoryTag value={issue.category} />
                <JiraLink jira_key={issue.jira_key} jira_url={issue.jira_url} />
              </div>
            </div>
            <div className={styles.statusLever}>
              <span className={styles.leverLabel}>Issue status</span>
              <MultiSelect
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === issue.status) || null}
                onChange={handleIssueStatus}
                isMulti={false}
                isClearable={false}
                isDisabled={savingStatus}
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <div className={styles.detailStats}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{issue.reports_count ?? reports.length}</span>
              <span className={styles.statLabel}>reports</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{formatDateTime(issue.last_reported_at)}</span>
              <span className={styles.statLabel}>last reported</span>
            </div>
          </div>
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Description</h3>
          {issue.description ? (
            <div
              className={styles.richText}
              dangerouslySetInnerHTML={{ __html: issue.description }}
            />
          ) : (
            <p className={styles.emptyText}>No description provided.</p>
          )}
        </section>
      </div>

      <aside className={styles.detailRight}>
        <div className={styles.rightHeader}>
          <h3 className={styles.sectionTitle}>
            Tenants affected
            {reports.length > 0 && <span className={styles.countPill}>{reports.length}</span>}
          </h3>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setShowAddTenant(true)}
          >
            + Add
          </button>
        </div>

        <div className={styles.reportsScroll}>
          {loadingReports && <p className={styles.emptyText}>Loading reports…</p>}
          {!loadingReports && reports.length === 0 && (
            <p className={styles.emptyText}>No tenants have reported this issue yet.</p>
          )}

          {reports.map((report) => (
            <div key={report.id || report._id} className={styles.reportRow}>
              <div className={styles.reportMain}>
                <span className={styles.reportTenant}>{report.tenant?.name || '—'}</span>
                {reporterName(report) && (
                  <span className={styles.reportReporter}>{reporterName(report)}</span>
                )}
                {report.details && <span className={styles.reportDetails}>{report.details}</span>}
                <span className={styles.reportMeta}>
                  Reported {formatDateTime(report.reported_at)}
                </span>
              </div>
              <div className={styles.reportStatus}>
                <MultiSelect
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.value === report.status) || null}
                  onChange={(option) => handleReportStatus(report, option)}
                  isMulti={false}
                  isClearable={false}
                  placeholder={labelFor(STATUS_OPTIONS, report.status)}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          ))}
        </div>
      </aside>

      <GmModal
        title="Add to tenant"
        show_title
        show_modal={showAddTenant}
        onClose={() => setShowAddTenant(false)}
      >
        <AddTenantToIssueDialog issue={issue} onAdded={handleTenantAdded} />
      </GmModal>
    </div>
  );
};

export default IssueDetailModal;
